// Attaches only the server-prepared approval presentation to its tool owner.
// No raw action arguments or tool identity cross this boundary.
import { z } from 'zod';

import { ehrPersonNoteEditorSchema } from '@apps/web/features/phoebe_chat_v3/model/ehr_person_note_editor';
import { settingsApprovalEditorSchema } from '@apps/web/features/phoebe_chat_v3/model/settings_approval';
import { shiftWriteEditorSchema } from '@apps/web/features/phoebe_chat_v3/model/shift_write_card';
import { approvalStatusPlacement } from '@apps/web/features/phoebe_chat_v3/registry/definitions/approval_renderer';
import { defineCompoundMember } from '@apps/web/features/phoebe_chat_v3/registry/types';

export const approvalPresentationSchema = z
    .object({
        question: z.string(),
        groups: z.array(
            z
                .object({
                    heading: z.string(),
                    items: z.array(z.string()),
                })
                .strict(),
        ),
    })
    .strict();
const caregiverTemplateValuesSchema = z
    .object({
        caregiver_first_name: z.string().max(200),
        caregiver_last_name: z.string().max(200),
        caregiver_name: z.string().max(200),
    })
    .strict();
export const approvalEditorSchema = z.discriminatedUnion('kind', [
    settingsApprovalEditorSchema,
    shiftWriteEditorSchema,
    ehrPersonNoteEditorSchema,
    z
        .object({
            kind: z.literal('sms'),
            recipients: z
                .array(
                    z
                        .object({
                            handle_type: z.enum([
                                'caregiver_id',
                                'conversation_id',
                            ]),
                            handle_id: z.uuid(),
                            name: z.string(),
                            message: z.string().max(1_600),
                            template_values:
                                caregiverTemplateValuesSchema.optional(),
                        })
                        .strict(),
                )
                .min(1),
        })
        .strict(),
    z
        .object({
            kind: z.literal('voice'),
            handle_type: z.enum(['caregiver_id', 'conversation_id']),
            handle_id: z.uuid(),
            name: z.string(),
            template_values: caregiverTemplateValuesSchema.optional(),
            instructions: z.string().max(2_000),
            opening_line: z.string().max(1_600).nullable(),
        })
        .strict(),
    z
        .object({
            kind: z.literal('assign_fill'),
            caregiver_id: z.uuid(),
            caregiver_name: z.string(),
            shifts: z
                .array(
                    z
                        .object({
                            id: z.uuid(),
                            name: z.string(),
                            detail: z.string(),
                            recurrence_label: z.string().nullable(),
                        })
                        .strict(),
                )
                .min(1)
                .max(100),
            notices: z
                .array(
                    z
                        .object({
                            text: z.string().max(200),
                            shift_ids: z.array(z.uuid()).max(100).nullable(),
                        })
                        .strict(),
                )
                .max(100)
                .optional(),
        })
        .strict(),
    z
        .object({
            kind: z.literal('save_note'),
        })
        .strict(),
    z
        .object({
            kind: z.literal('outreach'),
            shift_count: z.number().int().positive(),
            client_count: z.number().int().positive(),
            date_range: z.string(),
        })
        .strict(),
]);
export const toolApprovalRequiredPayloadSchema = z
    .object({
        command_id: z.uuid(),
        approval_reason: z.string().min(1).max(200).optional(),
        presentation: approvalPresentationSchema
            .nullable()
            .optional()
            .default(null),
        presentation_editor: approvalEditorSchema
            .nullable()
            .optional()
            .default(null),
        // Events written before the flag existed keep today's behavior.
        always_allow_available: z.boolean().optional().default(true),
    })
    .strip();

export const toolApprovalRequiredDefinitions = {
    tool_approval_required: defineCompoundMember('tool_approval_required', {
        disposition: 'compound_member',
        schema: toolApprovalRequiredPayloadSchema,
        malformed: { kind: 'skip' },
        evidence: null,
        slotMember: {
            slot: 'approval',
            terminal: false,
            keys(payload, occurrence) {
                const causedByEventId =
                    occurrence.committed?.caused_by_event_id;
                if (causedByEventId === null || causedByEventId === undefined) {
                    return [
                        {
                            kind: 'command_id',
                            value: payload.command_id,
                        },
                    ];
                }
                // The event key attaches this member; the command key is
                // retained as the alias used by its resolution event.
                return [
                    { kind: 'event_id', value: causedByEventId },
                    {
                        kind: 'command_id',
                        value: payload.command_id,
                    },
                ];
            },
            fragment(payload) {
                return {
                    kind: 'approval_required',
                    commandId: payload.command_id,
                    presentation: payload.presentation,
                    editor: payload.presentation_editor,
                    alwaysAllowAvailable: payload.always_allow_available,
                    ...(approvalStatusPlacement(
                        payload.presentation_editor,
                        payload.presentation,
                    ) === 'inline'
                        ? { statusPlacement: 'inline' as const }
                        : {}),
                };
            },
        },
    }),
} as const;

export const toolApprovalRequiredContractFixtures = {
    valid: {
        command_id: '0198a846-739e-7a2f-a152-66a77cc11111',
        presentation: {
            question: 'Allow this action?',
            groups: [
                {
                    heading: 'Changes',
                    items: ['Update the schedule'],
                },
            ],
        },
    },
    malformed: {
        command_id: 'not-a-uuid',
        presentation: { question: 'Allow this action?', groups: [] },
    },
} as const;
